import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api-client.js';
import { formatDate } from '@cardealeros/shared';
import { useState } from 'react';
import { useAuthStore } from '../stores/auth-store.js';

function UpgradeCard() {
  const [businessName, setBusinessName] = useState('');
  const [upgrading, setUpgrading] = useState(false);
  const [success, setSuccess] = useState(false);
  const setAuth = useAuthStore((s) => s.setAuth);
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);

  const handleUpgrade = async () => {
    if (!businessName.trim()) return;
    setUpgrading(true);
    const res = await api.patch<any>('/settings/dealership', { account_type: 'dealer', name: businessName.trim() });
    if (res.success && token && user) {
      setAuth(token, user, { ...res.data, account_type: 'dealer' });
      setSuccess(true);
    }
    setUpgrading(false);
  };

  if (success) {
    return (
      <div className="card p-5 mb-6 border border-green-800 bg-green-900/20">
        <p className="text-green-400 font-semibold">Upgraded! Reload to see all dealer features.</p>
      </div>
    );
  }

  return (
    <div id="upgrade" className="card p-5 mb-6 border border-brand-700 bg-brand-900/20">
      <h3 className="font-bold text-lg mb-1">Upgrade to Dealer Account</h3>
      <p className="text-surface-400 text-sm mb-4">
        Unlock CRM, deals, website builder, forms, multi-platform auto-posting, and more.
      </p>
      <input
        className="input w-full mb-3"
        placeholder="Business Name"
        value={businessName}
        onChange={(e) => setBusinessName(e.target.value)}
      />
      <button
        onClick={handleUpgrade}
        disabled={upgrading || !businessName.trim()}
        className="btn-primary w-full"
      >
        {upgrading ? 'Upgrading...' : 'Upgrade to Dealer'}
      </button>
    </div>
  );
}

type TabKey = 'account' | 'staff' | 'apikeys' | 'notifications';

export default function SettingsPage() {
  const isDealer = useAuthStore((s) => s.isDealer);
  const dealer = isDealer();
  const [activeTab, setActiveTab] = useState<TabKey>('account');

  const allTabs: { key: TabKey; label: string; dealerOnly: boolean }[] = [
    { key: 'account', label: dealer ? 'Dealership' : 'Account', dealerOnly: false },
    { key: 'staff', label: 'Staff', dealerOnly: true },
    { key: 'apikeys', label: 'API Keys', dealerOnly: true },
    { key: 'notifications', label: 'Notifications', dealerOnly: false },
  ];

  const tabs = allTabs.filter((t) => !t.dealerOnly || dealer);

  return (
    <div className="page">
      <h2 className="text-2xl font-bold mb-1">Settings</h2>
      <p className="text-surface-500 text-sm mb-6">{dealer ? 'Manage your dealership' : 'Manage your account'}</p>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto -mx-4 px-4">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.key
                ? 'bg-brand-600 text-white'
                : 'bg-surface-800 text-surface-400 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {!dealer && <UpgradeCard />}
      {activeTab === 'account' && <AccountSettings />}
      {activeTab === 'staff' && dealer && <StaffSettings />}
      {activeTab === 'apikeys' && dealer && <ApiKeySettings />}
      {activeTab === 'notifications' && <NotificationSettings />}
    </div>
  );
}

function AccountSettings() {
  const queryClient = useQueryClient();
  const isDealer = useAuthStore((s) => s.isDealer);
  const dealer = isDealer();

  const { data: res, isLoading } = useQuery({
    queryKey: ['settings-dealership'],
    queryFn: () => api.get<any>('/settings/dealership'),
  });

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, string>) => api.patch('/settings/dealership', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings-dealership'] });
      setEditing(false);
    },
  });

  const d = res?.data;
  if (isLoading) return <p className="text-surface-500 text-center py-10">Loading...</p>;
  if (!d) return <p className="text-surface-500 text-center py-10">Not found</p>;

  const startEdit = () => {
    setForm({
      name: d.name ?? '',
      phone: d.phone ?? '',
      address_line1: d.address_line1 ?? '',
      address_line2: d.address_line2 ?? '',
      city: d.city ?? '',
      state: d.state ?? '',
      zip: d.zip ?? '',
      ...(dealer ? { license_number: d.license_number ?? '' } : {}),
    });
    setEditing(true);
  };

  if (editing) {
    const fields = [
      { key: 'name', label: dealer ? 'Business Name' : 'Name' },
      { key: 'phone', label: 'Phone' },
      { key: 'address_line1', label: 'Address' },
      { key: 'address_line2', label: 'Address Line 2' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'zip', label: 'ZIP' },
      ...(dealer ? [{ key: 'license_number', label: 'Dealer License #' }] : []),
    ];

    return (
      <div className="space-y-3">
        {fields.map((f) => (
          <div key={f.key}>
            <label className="text-xs text-surface-500 mb-1 block">{f.label}</label>
            <input
              className="input w-full"
              value={form[f.key] ?? ''}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
            />
          </div>
        ))}
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => updateMutation.mutate(form)}
            disabled={updateMutation.isPending}
            className="btn-primary flex-1"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </button>
          <button onClick={() => setEditing(false)} className="btn-ghost flex-1">Cancel</button>
        </div>
      </div>
    );
  }

  const displayFields = [
    { label: 'Email', value: d.email },
    { label: 'Phone', value: d.phone ?? '—' },
    { label: 'Address', value: [d.address_line1, d.city, d.state, d.zip].filter(Boolean).join(', ') || '—' },
    ...(dealer ? [{ label: 'License #', value: d.license_number ?? '—' }] : []),
    { label: 'Tier', value: d.tier },
    ...(dealer ? [{ label: 'Subscription', value: d.subscription_status ?? 'none' }] : []),
  ];

  return (
    <div>
      <div className="card p-4 mb-4">
        <div className="flex justify-between items-start mb-4">
          <h3 className="font-semibold">{d.name}</h3>
          <button onClick={startEdit} className="text-brand-400 text-sm">Edit</button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {displayFields.map(({ label, value }) => (
            <div key={label}>
              <p className="text-xs text-surface-500">{label}</p>
              <p className="font-medium capitalize">{value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StaffSettings() {
  const queryClient = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'sales' });

  const { data: res } = useQuery({
    queryKey: ['settings-users'],
    queryFn: () => api.get<any>('/settings/users'),
  });

  const inviteMutation = useMutation({
    mutationFn: (data: { email: string; name: string; role: string }) =>
      api.post('/settings/users/invite', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings-users'] });
      setShowInvite(false);
      setInviteForm({ email: '', name: '', role: 'sales' });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/settings/users/${id}`, { is_active: active ? 1 : 0 }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings-users'] }),
  });

  const users = res?.data ?? [];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold">Staff Members</h3>
        <button onClick={() => setShowInvite(!showInvite)} className="text-brand-400 text-sm">
          {showInvite ? 'Cancel' : '+ Invite'}
        </button>
      </div>

      {showInvite && (
        <div className="card p-4 mb-4 space-y-3">
          <input
            className="input w-full"
            placeholder="Email"
            value={inviteForm.email}
            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
          />
          <input
            className="input w-full"
            placeholder="Full Name"
            value={inviteForm.name}
            onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
          />
          <select
            className="input w-full"
            value={inviteForm.role}
            onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
          >
            <option value="sales">Sales</option>
            <option value="finance">Finance</option>
            <option value="manager">Manager</option>
          </select>
          <button
            onClick={() => inviteMutation.mutate(inviteForm)}
            disabled={inviteMutation.isPending}
            className="btn-primary w-full"
          >
            {inviteMutation.isPending ? 'Inviting...' : 'Send Invite'}
          </button>
        </div>
      )}

      <div className="space-y-2">
        {users.map((u: any) => (
          <div key={u.id} className="card p-3">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-sm">{u.name}</p>
                <p className="text-xs text-surface-500">{u.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-surface-700 rounded text-xs capitalize">{u.role}</span>
                <button
                  onClick={() => toggleMutation.mutate({ id: u.id, active: !u.is_active })}
                  className={`w-8 h-5 rounded-full transition-colors relative ${
                    u.is_active ? 'bg-green-600' : 'bg-surface-600'
                  }`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                    u.is_active ? 'left-3.5' : 'left-0.5'
                  }`} />
                </button>
              </div>
            </div>
            {u.last_login_at && (
              <p className="text-xs text-surface-600 mt-1">Last login: {formatDate(u.last_login_at)}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ApiKeySettings() {
  const queryClient = useQueryClient();
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const { data: res } = useQuery({
    queryKey: ['settings-api-keys'],
    queryFn: () => api.get<any>('/settings/api-keys'),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => api.post<any>('/settings/api-keys', { name }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['settings-api-keys'] });
      setCreatedKey(data.data?.key ?? null);
      setNewKeyName('');
    },
  });

  const keys = res?.data ?? [];

  return (
    <div>
      <h3 className="font-semibold mb-4">API Keys</h3>

      {createdKey && (
        <div className="card p-4 mb-4 border-green-700 bg-green-900/10">
          <p className="text-green-400 text-sm font-medium mb-2">New API Key Created</p>
          <p className="text-xs text-surface-400 mb-2">Copy this key now — it won't be shown again.</p>
          <div className="bg-surface-800 rounded p-2 text-xs font-mono break-all">{createdKey}</div>
          <button onClick={() => setCreatedKey(null)} className="btn-ghost w-full mt-3 text-sm">Dismiss</button>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <input
          className="input flex-1"
          placeholder="Key name (optional)"
          value={newKeyName}
          onChange={(e) => setNewKeyName(e.target.value)}
        />
        <button
          onClick={() => createMutation.mutate(newKeyName || 'Default')}
          disabled={createMutation.isPending}
          className="btn-primary text-sm px-4"
        >
          {createMutation.isPending ? '...' : 'Create'}
        </button>
      </div>

      <div className="space-y-2">
        {keys.map((k: any) => (
          <div key={k.id} className="card p-3">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-sm">{k.name}</p>
                <p className="text-xs text-surface-500 font-mono">{k.key_preview}</p>
              </div>
              <span className={`w-2 h-2 rounded-full ${k.is_active ? 'bg-green-400' : 'bg-red-400'}`} />
            </div>
            {k.last_used_at && (
              <p className="text-xs text-surface-600 mt-1">Last used: {formatDate(k.last_used_at)}</p>
            )}
          </div>
        ))}
        {keys.length === 0 && !createdKey && (
          <p className="text-surface-500 text-center py-6 text-sm">No API keys yet</p>
        )}
      </div>
    </div>
  );
}

function NotificationSettings() {
  const queryClient = useQueryClient();

  const { data: res, isLoading } = useQuery({
    queryKey: ['notification-prefs'],
    queryFn: () => api.get<Record<string, boolean>>('/notifications/preferences'),
  });

  const updateMutation = useMutation({
    mutationFn: (prefs: Record<string, boolean>) => api.patch('/notifications/preferences', prefs),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notification-prefs'] }),
  });

  const prefs = res?.data ?? {};

  const PREF_LABELS: Record<string, string> = {
    new_lead: 'New Leads',
    deal_update: 'Deal Updates',
    listing_status: 'Listing Status Changes',
    vehicle_sold: 'Vehicle Sold',
    payment_alert: 'Payment Alerts',
  };

  const toggle = (key: string) => {
    updateMutation.mutate({ [key]: !prefs[key] });
  };

  if (isLoading) return <p className="text-surface-500 text-center py-10">Loading...</p>;

  return (
    <div>
      <h3 className="font-semibold mb-4">Push Notifications</h3>
      <div className="space-y-2">
        {Object.entries(PREF_LABELS).map(([key, label]) => (
          <div key={key} className="card p-3 flex items-center justify-between">
            <span className="text-sm">{label}</span>
            <button
              onClick={() => toggle(key)}
              className={`w-10 h-6 rounded-full transition-colors relative ${
                prefs[key] !== false ? 'bg-brand-600' : 'bg-surface-600'
              }`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                prefs[key] !== false ? 'left-5' : 'left-1'
              }`} />
            </button>
          </div>
        ))}
      </div>
      <p className="text-xs text-surface-500 mt-4">
        Push notifications require permission from your browser or device.
      </p>
    </div>
  );
}
